import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { sanitizePhoneInput, splitDialCode } from '@/lib/phone-number';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

// Complete country data with flags, codes, and dial codes
const countries = [
    { code: 'AF', name: 'Afghanistan', dialCode: '+93', flag: '🇦🇫' },
    { code: 'AX', name: 'Aland Islands', dialCode: '+358', flag: '🇦🇽' },
    { code: 'AL', name: 'Albania', dialCode: '+355', flag: '🇦🇱' },
    { code: 'DZ', name: 'Algeria', dialCode: '+213', flag: '🇩🇿' },
    { code: 'AS', name: 'American Samoa', dialCode: '+1684', flag: '🇦🇸' },
    { code: 'AD', name: 'Andorra', dialCode: '+376', flag: '🇦🇩' },
    { code: 'AO', name: 'Angola', dialCode: '+244', flag: '🇦🇴' },
    { code: 'AI', name: 'Anguilla', dialCode: '+1264', flag: '🇦🇮' },
    { code: 'AQ', name: 'Antarctica', dialCode: '+672', flag: '🇦🇶' },
    { code: 'AG', name: 'Antigua and Barbuda', dialCode: '+1268', flag: '🇦🇬' },
    { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
    { code: 'AM', name: 'Armenia', dialCode: '+374', flag: '🇦🇲' },
    { code: 'AW', name: 'Aruba', dialCode: '+297', flag: '🇦🇼' },
    { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
    { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
    { code: 'AZ', name: 'Azerbaijan', dialCode: '+994', flag: '🇦🇿' },
    { code: 'BS', name: 'Bahamas', dialCode: '+1242', flag: '🇧🇸' },
    { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
    { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
    { code: 'BB', name: 'Barbados', dialCode: '+1246', flag: '🇧🇧' },
    { code: 'BY', name: 'Belarus', dialCode: '+375', flag: '🇧🇾' },
    { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
    { code: 'BZ', name: 'Belize', dialCode: '+501', flag: '🇧🇿' },
    { code: 'BJ', name: 'Benin', dialCode: '+229', flag: '🇧🇯' },
    { code: 'BM', name: 'Bermuda', dialCode: '+1441', flag: '🇧🇲' },
    { code: 'BT', name: 'Bhutan', dialCode: '+975', flag: '🇧🇹' },
    { code: 'BO', name: 'Bolivia', dialCode: '+591', flag: '🇧🇴' },
    { code: 'BQ', name: 'Bonaire, Sint Eustatius and Saba', dialCode: '+599', flag: '🇧🇶' },
    { code: 'BA', name: 'Bosnia and Herzegovina', dialCode: '+387', flag: '🇧🇦' },
    { code: 'BW', name: 'Botswana', dialCode: '+267', flag: '🇧🇼' },
    { code: 'BV', name: 'Bouvet Island', dialCode: '+47', flag: '🇧🇻' },
    { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
    { code: 'IO', name: 'British Indian Ocean Territory', dialCode: '+246', flag: '🇮🇴' },
    { code: 'BN', name: 'Brunei Darussalam', dialCode: '+673', flag: '🇧🇳' },
    { code: 'BG', name: 'Bulgaria', dialCode: '+359', flag: '🇧🇬' },
    { code: 'BF', name: 'Burkina Faso', dialCode: '+226', flag: '🇧🇫' },
    { code: 'BI', name: 'Burundi', dialCode: '+257', flag: '🇧🇮' },
    { code: 'CV', name: 'Cabo Verde', dialCode: '+238', flag: '🇨🇻' },
    { code: 'KH', name: 'Cambodia', dialCode: '+855', flag: '🇰🇭' },
    { code: 'CM', name: 'Cameroon', dialCode: '+237', flag: '🇨🇲' },
    { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
    { code: 'KY', name: 'Cayman Islands', dialCode: '+1345', flag: '🇰🇾' },
    { code: 'CF', name: 'Central African Republic', dialCode: '+236', flag: '🇨🇫' },
    { code: 'TD', name: 'Chad', dialCode: '+235', flag: '🇹🇩' },
    { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
    { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
    { code: 'CX', name: 'Christmas Island', dialCode: '+61', flag: '🇨🇽' },
    { code: 'CC', name: 'Cocos (Keeling) Islands', dialCode: '+61', flag: '🇨🇨' },
    { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
    { code: 'KM', name: 'Comoros', dialCode: '+269', flag: '🇰🇲' },
    { code: 'CG', name: 'Congo', dialCode: '+242', flag: '🇨🇬' },
    { code: 'CD', name: 'Congo, Democratic Republic of the', dialCode: '+243', flag: '🇨🇩' },
    { code: 'CK', name: 'Cook Islands', dialCode: '+682', flag: '🇨🇰' },
    { code: 'CR', name: 'Costa Rica', dialCode: '+506', flag: '🇨🇷' },
    { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
    { code: 'HR', name: 'Croatia', dialCode: '+385', flag: '🇭🇷' },
    { code: 'CU', name: 'Cuba', dialCode: '+53', flag: '🇨🇺' },
    { code: 'CW', name: 'Curaçao', dialCode: '+599', flag: '🇨🇼' },
    { code: 'CY', name: 'Cyprus', dialCode: '+357', flag: '🇨🇾' },
    { code: 'CZ', name: 'Czech Republic', dialCode: '+420', flag: '🇨🇿' },
    { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
    { code: 'DJ', name: 'Djibouti', dialCode: '+253', flag: '🇩🇯' },
    { code: 'DM', name: 'Dominica', dialCode: '+1767', flag: '🇩🇲' },
    { code: 'DO', name: 'Dominican Republic', dialCode: '+1', flag: '🇩🇴' },
    { code: 'EC', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
    { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
    { code: 'SV', name: 'El Salvador', dialCode: '+503', flag: '🇸🇻' },
    { code: 'GQ', name: 'Equatorial Guinea', dialCode: '+240', flag: '🇬🇶' },
    { code: 'ER', name: 'Eritrea', dialCode: '+291', flag: '🇪🇷' },
    { code: 'EE', name: 'Estonia', dialCode: '+372', flag: '🇪🇪' },
    { code: 'SZ', name: 'Eswatini', dialCode: '+268', flag: '🇸🇿' },
    { code: 'ET', name: 'Ethiopia', dialCode: '+251', flag: '🇪🇹' },
    { code: 'FK', name: 'Falkland Islands (Malvinas)', dialCode: '+500', flag: '🇫🇰' },
    { code: 'FO', name: 'Faroe Islands', dialCode: '+298', flag: '🇫🇴' },
    { code: 'FJ', name: 'Fiji', dialCode: '+679', flag: '🇫🇯' },
    { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
    { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
    { code: 'GF', name: 'French Guiana', dialCode: '+594', flag: '🇬🇫' },
    { code: 'PF', name: 'French Polynesia', dialCode: '+689', flag: '🇵🇫' },
    { code: 'TF', name: 'French Southern Territories', dialCode: '+262', flag: '🇹🇫' },
    { code: 'GA', name: 'Gabon', dialCode: '+241', flag: '🇬🇦' },
    { code: 'GM', name: 'Gambia', dialCode: '+220', flag: '🇬🇲' },
    { code: 'GE', name: 'Georgia', dialCode: '+995', flag: '🇬🇪' },
    { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
    { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
    { code: 'GI', name: 'Gibraltar', dialCode: '+350', flag: '🇬🇮' },
    { code: 'GR', name: 'Greece', dialCode: '+30', flag: '🇬🇷' },
    { code: 'GL', name: 'Greenland', dialCode: '+299', flag: '🇬🇱' },
    { code: 'GD', name: 'Grenada', dialCode: '+1473', flag: '🇬🇩' },
    { code: 'GP', name: 'Guadeloupe', dialCode: '+590', flag: '🇬🇵' },
    { code: 'GU', name: 'Guam', dialCode: '+1671', flag: '🇬🇺' },
    { code: 'GT', name: 'Guatemala', dialCode: '+502', flag: '🇬🇹' },
    { code: 'GG', name: 'Guernsey', dialCode: '+44', flag: '🇬🇬' },
    { code: 'GN', name: 'Guinea', dialCode: '+224', flag: '🇬🇳' },
    { code: 'GW', name: 'Guinea-Bissau', dialCode: '+245', flag: '🇬🇼' },
    { code: 'GY', name: 'Guyana', dialCode: '+592', flag: '🇬🇾' },
    { code: 'HT', name: 'Haiti', dialCode: '+509', flag: '🇭🇹' },
    { code: 'HM', name: 'Heard Island and McDonald Islands', dialCode: '+672', flag: '🇭🇲' },
    { code: 'VA', name: 'Holy See', dialCode: '+379', flag: '🇻🇦' },
    { code: 'HN', name: 'Honduras', dialCode: '+504', flag: '🇭🇳' },
    { code: 'HK', name: 'Hong Kong', dialCode: '+852', flag: '🇭🇰' },
    { code: 'HU', name: 'Hungary', dialCode: '+36', flag: '🇭🇺' },
    { code: 'IS', name: 'Iceland', dialCode: '+354', flag: '🇮🇸' },
    { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
    { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
    { code: 'IR', name: 'Iran', dialCode: '+98', flag: '🇮🇷' },
    { code: 'IQ', name: 'Iraq', dialCode: '+964', flag: '🇮🇶' },
    { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪' },
    { code: 'IM', name: 'Isle of Man', dialCode: '+44', flag: '🇮🇲' },
    { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
    { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
    { code: 'JM', name: 'Jamaica', dialCode: '+1876', flag: '🇯🇲' },
    { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
    { code: 'JE', name: 'Jersey', dialCode: '+44', flag: '🇯🇪' },
    { code: 'JO', name: 'Jordan', dialCode: '+962', flag: '🇯🇴' },
    { code: 'KZ', name: 'Kazakhstan', dialCode: '+7', flag: '🇰🇿' },
    { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
    { code: 'KI', name: 'Kiribati', dialCode: '+686', flag: '🇰🇮' },
    { code: 'KP', name: "Korea, Democratic People's Republic of", dialCode: '+850', flag: '🇰🇵' },
    { code: 'KR', name: 'Korea, Republic of', dialCode: '+82', flag: '🇰🇷' },
    { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
    { code: 'KG', name: 'Kyrgyzstan', dialCode: '+996', flag: '🇰🇬' },
    { code: 'LA', name: "Lao People's Democratic Republic", dialCode: '+856', flag: '🇱🇦' },
    { code: 'LV', name: 'Latvia', dialCode: '+371', flag: '🇱🇻' },
    { code: 'LB', name: 'Lebanon', dialCode: '+961', flag: '🇱🇧' },
    { code: 'LS', name: 'Lesotho', dialCode: '+266', flag: '🇱🇸' },
    { code: 'LR', name: 'Liberia', dialCode: '+231', flag: '🇱🇷' },
    { code: 'LY', name: 'Libya', dialCode: '+218', flag: '🇱🇾' },
    { code: 'LI', name: 'Liechtenstein', dialCode: '+423', flag: '🇱🇮' },
    { code: 'LT', name: 'Lithuania', dialCode: '+370', flag: '🇱🇹' },
    { code: 'LU', name: 'Luxembourg', dialCode: '+352', flag: '🇱🇺' },
    { code: 'MO', name: 'Macao', dialCode: '+853', flag: '🇲🇴' },
    { code: 'MK', name: 'Macedonia, the former Yugoslav Republic of', dialCode: '+389', flag: '🇲🇰' },
    { code: 'MG', name: 'Madagascar', dialCode: '+261', flag: '🇲🇬' },
    { code: 'MW', name: 'Malawi', dialCode: '+265', flag: '🇲🇼' },
    { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
    { code: 'MV', name: 'Maldives', dialCode: '+960', flag: '🇲🇻' },
    { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
    { code: 'MT', name: 'Malta', dialCode: '+356', flag: '🇲🇹' },
    { code: 'MH', name: 'Marshall Islands', dialCode: '+692', flag: '🇲🇭' },
    { code: 'MQ', name: 'Martinique', dialCode: '+596', flag: '🇲🇶' },
    { code: 'MR', name: 'Mauritania', dialCode: '+222', flag: '🇲🇷' },
    { code: 'MU', name: 'Mauritius', dialCode: '+230', flag: '🇲🇺' },
    { code: 'YT', name: 'Mayotte', dialCode: '+262', flag: '🇾🇹' },
    { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
    { code: 'FM', name: 'Micronesia, Federated States of', dialCode: '+691', flag: '🇫🇲' },
    { code: 'MD', name: 'Moldova, Republic of', dialCode: '+373', flag: '🇲🇩' },
    { code: 'MC', name: 'Monaco', dialCode: '+377', flag: '🇲🇨' },
    { code: 'MN', name: 'Mongolia', dialCode: '+976', flag: '🇲🇳' },
    { code: 'ME', name: 'Montenegro', dialCode: '+382', flag: '🇲🇪' },
    { code: 'MS', name: 'Montserrat', dialCode: '+1664', flag: '🇲🇸' },
    { code: 'MA', name: 'Morocco', dialCode: '+212', flag: '🇲🇦' },
    { code: 'MZ', name: 'Mozambique', dialCode: '+258', flag: '🇲🇿' },
    { code: 'MM', name: 'Myanmar', dialCode: '+95', flag: '🇲🇲' },
    { code: 'NA', name: 'Namibia', dialCode: '+264', flag: '🇳🇦' },
    { code: 'NR', name: 'Nauru', dialCode: '+674', flag: '🇳🇷' },
    { code: 'NP', name: 'Nepal', dialCode: '+977', flag: '🇳🇵' },
    { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
    { code: 'NC', name: 'New Caledonia', dialCode: '+687', flag: '🇳🇨' },
    { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
    { code: 'NI', name: 'Nicaragua', dialCode: '+505', flag: '🇳🇮' },
    { code: 'NE', name: 'Niger', dialCode: '+227', flag: '🇳🇪' },
    { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
    { code: 'NU', name: 'Niue', dialCode: '+683', flag: '🇳🇺' },
    { code: 'NF', name: 'Norfolk Island', dialCode: '+672', flag: '🇳🇫' },
    { code: 'MP', name: 'Northern Mariana Islands', dialCode: '+1670', flag: '🇲🇵' },
    { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
    { code: 'OM', name: 'Oman', dialCode: '+968', flag: '🇴🇲' },
    { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
    { code: 'PW', name: 'Palau', dialCode: '+680', flag: '🇵🇼' },
    { code: 'PS', name: 'Palestine, State of', dialCode: '+970', flag: '🇵🇸' },
    { code: 'PA', name: 'Panama', dialCode: '+507', flag: '🇵🇦' },
    { code: 'PG', name: 'Papua New Guinea', dialCode: '+675', flag: '🇵🇬' },
    { code: 'PY', name: 'Paraguay', dialCode: '+595', flag: '🇵🇾' },
    { code: 'PE', name: 'Peru', dialCode: '+51', flag: '🇵🇪' },
    { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
    { code: 'PN', name: 'Pitcairn', dialCode: '+64', flag: '🇵🇳' },
    { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱' },
    { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
    { code: 'PR', name: 'Puerto Rico', dialCode: '+1', flag: '🇵🇷' },
    { code: 'QA', name: 'Qatar', dialCode: '+974', flag: '🇶🇦' },
    { code: 'RE', name: 'Réunion', dialCode: '+262', flag: '🇷🇪' },
    { code: 'RO', name: 'Romania', dialCode: '+40', flag: '🇷🇴' },
    { code: 'RU', name: 'Russian Federation', dialCode: '+7', flag: '🇷🇺' },
    { code: 'RW', name: 'Rwanda', dialCode: '+250', flag: '🇷🇼' },
    { code: 'BL', name: 'Saint Barthélemy', dialCode: '+590', flag: '🇧🇱' },
    { code: 'SH', name: 'Saint Helena, Ascension and Tristan da Cunha', dialCode: '+290', flag: '🇸🇭' },
    { code: 'KN', name: 'Saint Kitts and Nevis', dialCode: '+1869', flag: '🇰🇳' },
    { code: 'LC', name: 'Saint Lucia', dialCode: '+1758', flag: '🇱🇨' },
    { code: 'MF', name: 'Saint Martin (French part)', dialCode: '+590', flag: '🇲🇫' },
    { code: 'PM', name: 'Saint Pierre and Miquelon', dialCode: '+508', flag: '🇵🇲' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', dialCode: '+1784', flag: '🇻🇨' },
    { code: 'WS', name: 'Samoa', dialCode: '+685', flag: '🇼🇸' },
    { code: 'SM', name: 'San Marino', dialCode: '+378', flag: '🇸🇲' },
    { code: 'ST', name: 'Sao Tome and Principe', dialCode: '+239', flag: '🇸🇹' },
    { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
    { code: 'SN', name: 'Senegal', dialCode: '+221', flag: '🇸🇳' },
    { code: 'RS', name: 'Serbia', dialCode: '+381', flag: '🇷🇸' },
    { code: 'SC', name: 'Seychelles', dialCode: '+248', flag: '🇸🇨' },
    { code: 'SL', name: 'Sierra Leone', dialCode: '+232', flag: '🇸🇱' },
    { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
    { code: 'SX', name: 'Sint Maarten (Dutch part)', dialCode: '+1721', flag: '🇸🇽' },
    { code: 'SK', name: 'Slovakia', dialCode: '+421', flag: '🇸🇰' },
    { code: 'SI', name: 'Slovenia', dialCode: '+386', flag: '🇸🇮' },
    { code: 'SB', name: 'Solomon Islands', dialCode: '+677', flag: '🇸🇧' },
    { code: 'SO', name: 'Somalia', dialCode: '+252', flag: '🇸🇴' },
    { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
    { code: 'GS', name: 'South Georgia and the South Sandwich Islands', dialCode: '+500', flag: '🇬🇸' },
    { code: 'SS', name: 'South Sudan', dialCode: '+211', flag: '🇸🇸' },
    { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
    { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
    { code: 'SD', name: 'Sudan', dialCode: '+249', flag: '🇸🇩' },
    { code: 'SR', name: 'Suriname', dialCode: '+597', flag: '🇸🇷' },
    { code: 'SJ', name: 'Svalbard and Jan Mayen', dialCode: '+47', flag: '🇸🇯' },
    { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
    { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
    { code: 'SY', name: 'Syrian Arab Republic', dialCode: '+963', flag: '🇸🇾' },
    { code: 'TW', name: 'Taiwan, Province of China', dialCode: '+886', flag: '🇹🇼' },
    { code: 'TJ', name: 'Tajikistan', dialCode: '+992', flag: '🇹🇯' },
    { code: 'TZ', name: 'Tanzania, United Republic of', dialCode: '+255', flag: '🇹🇿' },
    { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
    { code: 'TL', name: 'Timor-Leste', dialCode: '+670', flag: '🇹🇱' },
    { code: 'TG', name: 'Togo', dialCode: '+228', flag: '🇹🇬' },
    { code: 'TK', name: 'Tokelau', dialCode: '+690', flag: '🇹🇰' },
    { code: 'TO', name: 'Tonga', dialCode: '+676', flag: '🇹🇴' },
    { code: 'TT', name: 'Trinidad and Tobago', dialCode: '+1868', flag: '🇹🇹' },
    { code: 'TN', name: 'Tunisia', dialCode: '+216', flag: '🇹🇳' },
    { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
    { code: 'TM', name: 'Turkmenistan', dialCode: '+993', flag: '🇹🇲' },
    { code: 'TC', name: 'Turks and Caicos Islands', dialCode: '+1649', flag: '🇹🇨' },
    { code: 'TV', name: 'Tuvalu', dialCode: '+688', flag: '🇹🇻' },
    { code: 'UG', name: 'Uganda', dialCode: '+256', flag: '🇺🇬' },
    { code: 'UA', name: 'Ukraine', dialCode: '+380', flag: '🇺🇦' },
    { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
    { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
    { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
    { code: 'UM', name: 'United States Minor Outlying Islands', dialCode: '+1', flag: '🇺🇲' },
    { code: 'UY', name: 'Uruguay', dialCode: '+598', flag: '🇺🇾' },
    { code: 'UZ', name: 'Uzbekistan', dialCode: '+998', flag: '🇺🇿' },
    { code: 'VU', name: 'Vanuatu', dialCode: '+678', flag: '🇻🇺' },
    { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
    { code: 'VN', name: 'Viet Nam', dialCode: '+84', flag: '🇻🇳' },
    { code: 'VG', name: 'Virgin Islands, British', dialCode: '+1284', flag: '🇻🇬' },
    { code: 'VI', name: 'Virgin Islands, U.S.', dialCode: '+1340', flag: '🇻🇮' },
    { code: 'WF', name: 'Wallis and Futuna', dialCode: '+681', flag: '🇼🇫' },
    { code: 'EH', name: 'Western Sahara', dialCode: '+212', flag: '🇪🇭' },
    { code: 'YE', name: 'Yemen', dialCode: '+967', flag: '🇾🇪' },
    { code: 'ZM', name: 'Zambia', dialCode: '+260', flag: '🇿🇲' },
    { code: 'ZW', name: 'Zimbabwe', dialCode: '+263', flag: '🇿🇼' },
];

const DIAL_CODES: readonly string[] = countries.map((c) => c.dialCode);

/**
 * Several countries share a dial code (+1 is the US, Canada, Puerto Rico…). Keep the
 * current selection when it already matches, then the form's default, then the first.
 */
function countryForDialCode(dialCode: string, current: (typeof countries)[0], fallbackCode: string): (typeof countries)[0] | undefined {
    const matches = countries.filter((c) => c.dialCode === dialCode);

    return matches.find((c) => c.code === current.code) ?? matches.find((c) => c.code === fallbackCode) ?? matches[0];
}

interface PhoneInputProps {
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    id?: string;
    name?: string;
    required?: boolean;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
    defaultCountry?: string;
    
    // Button styling
    buttonClassName?: string;
    buttonStyle?: React.CSSProperties;
    buttonVariant?: 'default' | 'outline' | 'ghost' | 'secondary';
    
    // Input styling
    inputClassName?: string;
    inputStyle?: React.CSSProperties;
    
    // Container styling
    style?: React.CSSProperties;
    
    // Popover content styling
    popoverClassName?: string;
    popoverStyle?: React.CSSProperties;
    
    // Command/search input styling
    searchInputClassName?: string;
    searchInputStyle?: React.CSSProperties;
    
    // List item styling
    itemClassName?: string;
    itemStyle?: React.CSSProperties;
}

export function PhoneInput({
    value = '',
    onChange,
    placeholder = '(555) 123-4567',
    disabled = false,
    className,
    id,
    name,
    required,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
    defaultCountry = 'US',
    buttonClassName,
    buttonStyle,
    buttonVariant = 'outline',
    inputClassName,
    inputStyle,
    style,
    popoverClassName,
    popoverStyle,
    searchInputClassName,
    searchInputStyle,
    itemClassName,
    itemStyle,
}: PhoneInputProps) {
    const [open, setOpen] = React.useState(false);
    const [selectedCountry, setSelectedCountry] = React.useState(() => {
        return countries.find((c) => c.code === defaultCountry) || countries.find((c) => c.code === 'US') || countries[0];
    });
    const [phoneNumber, setPhoneNumber] = React.useState('');
    const [searchValue, setSearchValue] = React.useState('');

    // Parse the value to extract country code and phone number
    React.useEffect(() => {
        if (value) {
            // Find all countries whose dial code matches the start of the value
            const candidates = countries.filter((c) => value.startsWith(c.dialCode));
            if (candidates.length > 0) {
                // Prefer the longest matching dial code first (e.g., +1868 over +1).
                // Among equal-length matches, prefer the currently selected country,
                // then the defaultCountry, to avoid US → Canada flipping on +1.
                candidates.sort((a, b) => {
                    const lenDiff = b.dialCode.length - a.dialCode.length;
                    if (lenDiff !== 0) return lenDiff;
                    if (a.code === selectedCountry.code) return -1;
                    if (b.code === selectedCountry.code) return 1;
                    if (a.code === defaultCountry) return -1;
                    if (b.code === defaultCountry) return 1;
                    return 0;
                });
                const best = candidates[0];
                setSelectedCountry(best);
                // trimStart, not trim: a trailing space is the one the person just typed
                // between digit groups, and trimming it here ate it on the next render.
                setPhoneNumber(value.substring(best.dialCode.length).trimStart());
            } else {
                // If no country code in value, just set the phone number
                setPhoneNumber(value);
            }
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    // Format and combine country code with phone number.
    //
    // Pasted numbers are the hazard: a number copied from a phone's contacts arrives as
    // "‪+1 (650) 350‑8802‬" — its own "+1", bidi control marks, a non-breaking hyphen —
    // and this handler used to emit the selector's "+1" on top of all of it. Zoho rejects
    // that whole lead (application 713, 2026-09-03) and the applicant never sees an error.
    // Strip what a phone number cannot contain, and when the paste brings a dial code,
    // move it into the selector instead of doubling it.
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { dialCode, number } = splitDialCode(sanitizePhoneInput(e.target.value), DIAL_CODES);

        let country = selectedCountry;
        if (dialCode && dialCode !== selectedCountry.dialCode) {
            country = countryForDialCode(dialCode, selectedCountry, defaultCountry) ?? selectedCountry;
            setSelectedCountry(country);
        }

        setPhoneNumber(number);
        onChange?.(number ? `${country.dialCode} ${number}` : '');
    };

    const handleCountrySelect = (country: typeof countries[0]) => {
        setSelectedCountry(country);
        setOpen(false);
        setSearchValue('');
        if (onChange && phoneNumber) {
            onChange(`${country.dialCode} ${phoneNumber}`);
        }
    };

    const filteredCountries = React.useMemo(() => {
        if (!searchValue) return countries;
        const search = searchValue.toLowerCase();
        return countries.filter(
            (country) =>
                country.name.toLowerCase().includes(search) ||
                country.dialCode.includes(search) ||
                country.code.toLowerCase().includes(search)
        );
    }, [searchValue]);

    return (
        <div className={cn('flex gap-2', className)} style={style}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant={buttonVariant}
                        role="combobox"
                        aria-expanded={open}
                        aria-label="Select country"
                        disabled={disabled}
                        className={cn('min-w-12 justify-center font-normal px-2', buttonClassName)}
                        style={buttonStyle}
                        type="button"
                    >
                        <span className="text-cms-body font-medium tabular-nums">{selectedCountry.dialCode}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className={cn("w-[320px] p-0", popoverClassName)} style={popoverStyle} align="start">
                    <Command>
                        <CommandInput 
                            placeholder="Search country..." 
                            value={searchValue}
                            onValueChange={setSearchValue}
                            className={searchInputClassName}
                            style={searchInputStyle}
                        />
                        <CommandList>
                            <CommandEmpty>No country found.</CommandEmpty>
                            <CommandGroup>
                                {filteredCountries.map((country) => (
                                    <CommandItem
                                        key={country.code}
                                        value={country.name}
                                        onSelect={() => handleCountrySelect(country)}
                                        className={cn("flex items-center gap-2", itemClassName)}
                                        style={itemStyle}
                                    >
                                        <Check
                                            className={cn(
                                                'mr-2 h-4 w-4',
                                                selectedCountry.code === country.code ? 'opacity-100' : 'opacity-0'
                                            )}
                                        />
                                        <span className="text-lg">{country.flag}</span>
                                        <span className="flex-1">{country.name}</span>
                                        <span className="text-muted-foreground">{country.dialCode}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            <Input
                type="tel"
                id={id}
                name={name}
                value={phoneNumber}
                onChange={handlePhoneChange}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                aria-invalid={ariaInvalid}
                aria-describedby={ariaDescribedBy}
                className={cn('flex-1', inputClassName)}
                style={inputStyle}
            />
        </div>
    );
}